// Cross-cutting constants. Every string here is either an external contract
// (the Point Click Care CSV header, the three decision-rejection reasons) or a
// value a reviewer will diff against `docs/`. Change one only on purpose.
//
// `FLAG_TYPES` below is the source of truth for flag wording that `docs/flags.md`
// mirrors; the database's `flag_definitions` table is seeded from the same
// strings. Keep all three in step.

import {
  ASSIGNMENT_ROLE,
  DECISION_OUTCOME,
  DECISION_STEP,
  FLAG_RESOLUTION,
  INVOICE_STATUS,
  PIPELINE_STEP_KEY,
  REASON_CATEGORY,
  type AssignmentRole,
  type DecisionOutcome,
  type DecisionStep,
  type FlagResolution,
  type InvoiceStatus,
  type PipelineStepKey,
  type ReasonCategory,
  type ApprovalRequirement,
} from './enums.js'
import type { RequiredApprovals } from './dto.js'

// ---------------------------------------------------------------------------
// Decision rejection reasons — persisted verbatim in the source, now carried in
// the 4xx `message`. The dash in the out-of-order string is an EM DASH (U+2014).
// ---------------------------------------------------------------------------

/** An active hold blocks every status advance and every GL edit. HTTP 409. */
export const REASON_BLOCKED_BY_HOLD = 'Blocked by an active hold'

/** The actor holds no authority for the step being decided. HTTP 403. */
export const REASON_NOT_AUTHORIZED_FOR_STEP = 'Not authorized for this step'

/**
 * Status/step mismatch. HTTP 409. Interpolates the record's ACTUAL current
 * status, which is what makes the message actionable.
 */
export function reasonOutOfOrder(currentStatus: InvoiceStatus | string): string {
  return `Out of order — record status is ${currentStatus}`
}

// ---------------------------------------------------------------------------
// GL-code edit rejection reasons (the line-item-edit endpoint), verbatim.
// ---------------------------------------------------------------------------

export const REASON_STEP_NOT_EDITABLE = 'Record is not at an editable approval step'
export const REASON_NOT_AUTHORIZED_TO_EDIT = 'Not authorized to edit at this step'

// ---------------------------------------------------------------------------
// Appointment validation reasons (the assignments endpoint), verbatim.
// ---------------------------------------------------------------------------

export const REASON_MISSING_USER = 'Missing user'
export const REASON_INVALID_ACTION = 'Invalid action'
export const REASON_INVALID_ROLE = 'Invalid role'
export const REASON_INVALID_WORKSPACE = 'Invalid workspace'
export const REASON_MISSING_COMPANY = 'Missing company'
export const REASON_TARGET_NOT_FOUND = 'Target assignment not found or already Inactive'
export const REASON_NOT_AUTHORIZED_TO_APPOINT = 'Not authorized to appoint this role'
/** 409 `duplicate_key`: the person already holds this role for this company, or
    the org-wide Oversight seat (owner, 2026-09-29 — the same person could be
    added to one role twice). No company in the words, because Oversight has none. */
export const REASON_ALREADY_ASSIGNED = 'This person already holds this role'

// ---------------------------------------------------------------------------
// Import Control / Denial Review outcome refusals, per the ratified rulings.
// ---------------------------------------------------------------------------

export const REASON_IMPORT_CONTROL_NO_DENY =
  'Import Control has no deny outcome; archive the record or release it'

/**
 * Hold is placed only at Import Control, the approval tiers, Export Control
 * and Denial Review (owner, 2026-09-30 — D-243, then D-247, which gave Export
 * Control Hold back, then 2026-10-08 — D-327, which gave Denial Review Hold
 * back). A hold placed or released at Closing Confirmation is refused with
 * this reason (`canPlaceOrReleaseHold`). Release used to stay open there for
 * holds placed before 2026-09-30; none can exist (code review C-13).
 */
export const REASON_NO_NEW_HOLDS_AT_STEP =
  'Holds are placed only at Import Control, the approval tiers, Export Control and Denial Review; release an existing hold or act on the invoice'

export function reasonUnsupportedOutcome(step: DecisionStep, outcome: string): string {
  return `${outcome} is not a supported outcome at ${step}`
}

// ---------------------------------------------------------------------------
// Status sets
// ---------------------------------------------------------------------------

/**
 * "Reached Point Click Care." Every precedent query keys on exactly this pair:
 * a code or a vendor becomes precedent only once the invoice carrying it
 * actually completed.
 */
export const TERMINAL_PCC_STATUSES: readonly InvoiceStatus[] = [
  INVOICE_STATUS.ImportedIntoPointClickCare,
  INVOICE_STATUS.TypedIntoPointClickCare,
]

/**
 * THE SIX ARCHIVES, one per answer to the Archive pop-up's Category (owner,
 * 2026-10-14 — docs/decisions.md D-362), in the order the pop-up offers them.
 * The answer IS the status: an Archive decision's `reason_category` decides
 * which of these the invoice reaches (`ARCHIVE_STATUS_FOR_CATEGORY`).
 */
export const ARCHIVE_STATUSES: readonly InvoiceStatus[] = [
  INVOICE_STATUS.Blank,
  INVOICE_STATUS.DifferentTypeOfDocument,
  INVOICE_STATUS.Duplicate,
  INVOICE_STATUS.PaymentDenied,
  INVOICE_STATUS.RevisedInvoiceRequested,
  INVOICE_STATUS.Other,
]

/** Which archive status each Archive Category leads to. */
export const ARCHIVE_STATUS_FOR_CATEGORY: Readonly<Partial<Record<ReasonCategory, InvoiceStatus>>> = {
  [REASON_CATEGORY.blank]: INVOICE_STATUS.Blank,
  [REASON_CATEGORY.different_type_of_document]: INVOICE_STATUS.DifferentTypeOfDocument,
  [REASON_CATEGORY.duplicate]: INVOICE_STATUS.Duplicate,
  [REASON_CATEGORY.payment_denied]: INVOICE_STATUS.PaymentDenied,
  [REASON_CATEGORY.revised_invoice_requested]: INVOICE_STATUS.RevisedInvoiceRequested,
  [REASON_CATEGORY.other]: INVOICE_STATUS.Other,
}

/** Every status from which nothing further can happen: the two completions, then the six archives. */
export const TERMINAL_STATUSES: readonly InvoiceStatus[] = [
  INVOICE_STATUS.ImportedIntoPointClickCare,
  INVOICE_STATUS.TypedIntoPointClickCare,
  ...ARCHIVE_STATUSES,
]

/**
 * The four approval steps, in the fixed order a route visits them
 * (`nextRequiredStatus`, `backend/src/domain/routing.ts`). Controller Approval
 * and Manager (Department) Approval are ALTERNATIVES in one stage, never both
 * (owner, 2026-10-07 — D-303). Which steps an invoice visits is its route,
 * decided once at the release or a reinstate and stored on the invoice (D-350):
 * an Override, else the matching routing rules, else the default route of
 * Controller Approval then Director Approval. Who is seated is not an input
 * (D-336). Import Control, the one editing and releasing step since AP/AR
 * Approval was merged into it (owner, 2026-09-29 — D-215), comes before them
 * and is authorized by a user role, not a seat.
 */
export const APPROVAL_TIER_STEPS: readonly DecisionStep[] = [
  DECISION_STEP.ControllerApproval,
  DECISION_STEP.DepartmentApproval,
  DECISION_STEP.DirectorApproval,
  DECISION_STEP.OversightApproval,
]

/**
 * MAY A LINE'S GL CODE CHANGE AT THIS STATUS? Both halves answer the same, on
 * every route:
 *
 *   Import Control, Controller Approval, Export Control   both halves
 *   every other status                                    neither
 *
 * THE OWNER'S RULES (2026-10-07). D-305 locked the halves by route. D-308 then
 * opened both at Export Control (*"Export Control should be able to edit
 * Account and Department values; however, at this point in the process, these
 * values should already be filled due to prior steps' requirements."*), and
 * D-309 locked the Department after Import Control on the Manager route too
 * (*"For the Manager path, Account and Department should now be accessible and
 * required at Import Control and Export Control, but both fields should be
 * locked elsewhere. Manager Approval is no longer the step for assigning the
 * Department; that is to happen now at Import Control."*), and later the same
 * day opened both at Manual Entry too (*You can allow Account and Department (and therefore Marketing Category) changes at Manual Entry, as well."*),
 * until the Manual Entry step was dropped (D-311). With both halves
 * required at Import Control's release and at Controller Approval's approve,
 * nothing between them and Export Control has a half to fill, so the route no
 * longer changes the answer and is no longer read.
 *
 * WHERE, not WHO: the server's `glEditAuthorityForStatus` adds the authority.
 * HERE, NOT IN THE BACKEND, so the server's check and the app's hint are one
 * function (D-274).
 */
export function glCodeHalfOpen(status: string): boolean {
  return (
    status === INVOICE_STATUS.PendingImportControlDecision ||
    status === INVOICE_STATUS.PendingControllerApprovalDecision ||
    status === INVOICE_STATUS.PendingExportControlDecision
  )
}

/**
 * The assignment roles that approve an invoice on a company's behalf — the
 * four tiers. The two Analytics seats are deliberately absent: they decide
 * nothing.
 */
export const APPROVAL_ASSIGNMENT_ROLES: readonly AssignmentRole[] = [
  ASSIGNMENT_ROLE.oversight,
  ASSIGNMENT_ROLE.director,
  ASSIGNMENT_ROLE.departmentHead,
  ASSIGNMENT_ROLE.controller,
]

/**
 * Outcomes that must carry an EXPLANATION before they commit — all but
 * `approved`. What counts as the explanation depends on the outcome:
 *
 *   * The three a person picks from a dialog — `denied`,
 *     `archived`, `requiresManualEntry` — take a **category**
 *     (`REASON_CATEGORIES_FOR_OUTCOME`), and the free-text reason is required
 *     only when that category is `other` (owner, 2026-09-15: "make the Reason
 *     required only if Other is chosen as the category"). A named category IS
 *     the explanation, and a better one than prose, because it can be counted.
 *   * The rest — the two Denial Review archives, `reinstated` and `escalated` —
 *     still require the reason itself. `reinstated` (owner, 2026-09-25) sends a
 *     denied invoice back to the first qualifying approval step (D-324), and
 *     the reason is the only thing that tells its approver why it is back. Denial Review's two outcomes each already say in their
 *     own dialog title what they are, so the owner's three-dialog ruling does
 *     not reach them; `escalated` was written only by the escalation worker,
 *     with a fixed reason, until D-336 removed it.
 *
 * `validateReason` (`backend/src/domain/decisionEngine.ts`) is the one place
 * that resolves this, and the `decisions_reason_required` CHECK in
 * `backend/db/schema.sql` is its database twin.
 */
export const REASON_REQUIRED_OUTCOMES: readonly DecisionOutcome[] = [
  DECISION_OUTCOME.denied,
  DECISION_OUTCOME.archivedWithoutPayment,
  DECISION_OUTCOME.archivedForFreshInvoice,
  DECISION_OUTCOME.archived,
  // Diverting an invoice off the import pathway is a routing change somebody
  // downstream has to understand, and the reason is the only record of WHY the
  // CSV did not fit (owner, 2026-08-31).
  DECISION_OUTCOME.requiresManualEntry,
  DECISION_OUTCOME.escalated,
  DECISION_OUTCOME.reinstated,
]

/**
 * The categories each outcome's dialog offers, and the only values the server
 * will store against it (owner, 2026-09-15).
 *
 * `other` is last in every list and is the one that turns the free-text reason
 * back on: picking it is saying "none of these", which is exactly when prose is
 * the only thing left that can explain the decision.
 *
 * An outcome absent from this map takes no category at all.
 */
export const REASON_CATEGORIES_FOR_OUTCOME: Readonly<
  Partial<Record<DecisionOutcome, readonly ReasonCategory[]>>
> = {
  [DECISION_OUTCOME.denied]: [
    REASON_CATEGORY.price_discrepancy,
    REASON_CATEGORY.wrong_company,
    REASON_CATEGORY.duplicate,
    REASON_CATEGORY.unauthorized_purchase,
    REASON_CATEGORY.other,
  ],
  // The Archive dialog (owner, 2026-09-15: "Offer Category options of Blank,
  // Duplicate, Not An Invoice, and Other"). Six since 2026-10-14 (D-362): Not
  // An Invoice reads Different Type Of Document, Payment Denied and Revised
  // Invoice Requested joined, and each answer is the status the invoice
  // reaches (`ARCHIVE_STATUS_FOR_CATEGORY` above). `duplicate` is offered here, which
  // is also what the one-click **Archive invoice** shortcut on a duplicate flag
  // row has always written — that control has no picker, and its stored value
  // is now one a person can pick too.
  [DECISION_OUTCOME.archived]: [
    REASON_CATEGORY.blank,
    REASON_CATEGORY.different_type_of_document,
    REASON_CATEGORY.duplicate,
    REASON_CATEGORY.payment_denied,
    REASON_CATEGORY.revised_invoice_requested,
    REASON_CATEGORY.other,
  ],
  // Typed Into Point Click Care (owner, 2026-10-07 — D-315: "change
  // the options to "PCC Import Error" and "Other""). Split Between Companies,
  // Move To Manual Entry's first answer, is no longer offered or accepted.
  [DECISION_OUTCOME.requiresManualEntry]: [
    REASON_CATEGORY.pcc_import_error,
    REASON_CATEGORY.other,
  ],
  // THE TWO DENIAL REVIEW ARCHIVES KEEP THE DENIAL VOCABULARY, and keep it
  // OPTIONAL — they are not among the three dialogs the owner named, and each
  // already says in its own title which of the two archives it is. Listed
  // explicitly rather than left to fall through, so the new Not An Invoice and
  // Move To Manual Entry values are not silently storable against them.
  [DECISION_OUTCOME.archivedWithoutPayment]: [
    REASON_CATEGORY.price_discrepancy,
    REASON_CATEGORY.wrong_company,
    REASON_CATEGORY.duplicate,
    REASON_CATEGORY.unauthorized_purchase,
    REASON_CATEGORY.other,
  ],
  [DECISION_OUTCOME.archivedForFreshInvoice]: [
    REASON_CATEGORY.price_discrepancy,
    REASON_CATEGORY.wrong_company,
    REASON_CATEGORY.duplicate,
    REASON_CATEGORY.unauthorized_purchase,
    REASON_CATEGORY.other,
  ],
}

/**
 * The Hold dialog's categories (owner, 2026-09-25), in the order it offers
 * them. Stored on `holds.reason_category`. REQUIRED on every new hold, with the
 * free-text reason required only under `other` — the same rule the Deny,
 * Archive and Move To Manual Entry dialogs follow, enforced by
 * `POST /invoices/{invoice_id}/holds` (`backend/src/http/routes/holds.ts`).
 */
export const HOLD_CATEGORIES: readonly ReasonCategory[] = [
  REASON_CATEGORY.awaiting_vendor_response,
  REASON_CATEGORY.awaiting_internal_information,
  REASON_CATEGORY.disputed_charge,
  REASON_CATEGORY.other,
]

/**
 * The outcomes whose category is REQUIRED (owner, 2026-09-15) — the three a
 * person picks from a dialog. The two Denial Review archives take a category
 * but do not demand one; see the map above.
 */
export const CATEGORY_REQUIRED_OUTCOMES: readonly DecisionOutcome[] = [
  DECISION_OUTCOME.denied,
  DECISION_OUTCOME.archived,
  DECISION_OUTCOME.requiresManualEntry,
]

/**
 * Denial-shaped outcomes for the Analytics denial panels; `app/src/lib/metrics.ts`
 * imports it. `escalated` is deliberately absent: an automatic movement is
 * not a denial and must not be counted as one on the Analytics screen.
 */
export const DENIAL_OUTCOMES: readonly DecisionOutcome[] = [
  DECISION_OUTCOME.denied,
  DECISION_OUTCOME.archivedWithoutPayment,
  DECISION_OUTCOME.archivedForFreshInvoice,
  DECISION_OUTCOME.archived,
]

/**
 * THE DEFAULT ROUTE: Controller Approval, then Director Approval, then Export
 * Control (owner, 2026-10-12 — D-350: "Let's change the default route from
 * Manager Approval to Controller Approval. It is probably better that an
 * accidental release keep information with the centralized controllers than
 * forward it to the companies."). From D-336 (2026-10-09) until then it was
 * Manager Approval and Director Approval. It is used when no Override is set
 * and no routing rule matches, and it is also what a new rule's checkboxes and
 * the Override pop-up's start at ("Controller Approval is the new default
 * starting point, and that should be reflected across the board"). The
 * database defaults on `oversight_rules` say the same.
 */
export const DEFAULT_REQUIRED_APPROVALS: Readonly<RequiredApprovals> = {
  requires_controller: true,
  requires_manager: false,
  requires_director: true,
  requires_oversight: false,
}

/** Each approval step's flag on a rule, an invoice and the wire (D-336). */
export const REQUIRED_APPROVAL_FIELD: Readonly<Record<ApprovalRequirement, keyof RequiredApprovals>> = {
  controller: 'requires_controller',
  manager: 'requires_manager',
  director: 'requires_director',
  oversight: 'requires_oversight',
}

// THE ESCALATION RULE OFF MANAGER APPROVAL IS GONE (owner, 2026-10-09 —
// D-336: "Every step becomes required now, as every step is intentional
// now."). Its worker skipped Manager Approval after three days without a
// decision; a skip of a required step is what the owner ruled out, so the
// worker, its schedule and its constants were removed. The `escalated` rows it
// wrote keep their stored step and reason, and History still shows them.

/**
 * Pipeline order of the timeline nodes, which is also the order a reminder digest
 * lists its rows.
 */
export const PIPELINE_STEP_ORDER: readonly PipelineStepKey[] = [
  PIPELINE_STEP_KEY.importControl,
  PIPELINE_STEP_KEY.controller,
  PIPELINE_STEP_KEY.department,
  PIPELINE_STEP_KEY.director,
  PIPELINE_STEP_KEY.oversight,
  PIPELINE_STEP_KEY.exportControl,
  PIPELINE_STEP_KEY.closingConfirmation,
  PIPELINE_STEP_KEY.denialReview,
]

/**
 * THERE IS NO ANCHOR COMPANY, AND NO COMPANY ID MEANS MORE THAN ITSELF (owner,
 * 2026-09-22 — `docs/decisions.md` D-134).
 *
 * Two constants stood here until then, `OTHER_COMPANY_NAME` and
 * `ALL_COMPANY_NAME`, naming two `companies` rows that were not companies. Both
 * existed for one mechanical reason: `assignments.company_id` is `text
 * REFERENCES companies(company_id)`, so an appointment that was not about one
 * company still needed something to point at.
 *
 *   * `Other` was a Dataverse TEAM, used to group companies together while
 *     testing because a Microsoft team per company was burdensome. It is absent
 *     from the company master and was never a company.
 *   * `All` was the org-wide Oversight seat, stored as a company for seven days.
 *
 * An org-wide appointment now says so in its own column,
 * `assignments.company_scope = 'organization'`, and names no company at all. So
 * there is nothing for a company picker to hide, nothing for a write path to
 * refuse, and no `isAnchorCompanyId` to remember to call.
 *
 * An invoice whose company is unknown carries `company_id IS NULL` and the
 * `MissingCompany` flag. It is not filed against anything, and never was.
 */

/**
 * PACE Healthcare Transportation (`companies.company_id` '156') — the one
 * company that runs a fleet. An invoice for it needs a Transportation Category
 * on each line (D-190), which is what matching, the required-fields check and
 * the app read this for.
 */
export const FLEET_COMPANY_ID = '156'
export const FLEET_COMPANY_NAME = 'PACE Healthcare Transportation'

// ---------------------------------------------------------------------------
// Numeric limits and thresholds
// ---------------------------------------------------------------------------

/**
 * Line items stored per invoice. A Power Automate loop-scale accommodation in
 * the source, kept as configuration because three behaviours hang off it:
 * `line_items_capped`, the `MaximumLineItemsExtracted` flag, and the suppression of
 * `LineItemsToTotalMismatch`. Raise it deliberately, never by accident.
 */
export const LINE_ITEM_CAP = 40

/** Below this, a confidence raises its entity's low-confidence flag. */
export const CONFIDENCE_FLAG_THRESHOLD = 90

/** A missing confidence is treated as fully confident, so it raises nothing. */
export const MISSING_CONFIDENCE_DEFAULT = 100

/**
 * The Account confidence stored on a line whose Account came from a
 * deterministic source — a vendor or general coding rule (arbitrated or not), a
 * Code printed on the page, or this vendor's identical past line (owner,
 * 2026-09-25, `docs/decisions.md` D-144). Such a line never raises
 * `AccountMatchLowConfidence`: the model's own score describes a guess the host
 * did not use. Always at or above `CONFIDENCE_FLAG_THRESHOLD`.
 */
export const DETERMINISTIC_ACCOUNT_CONFIDENCE = 100

/**
 * How far apart two invoice dates may be for "same total, a few days apart" to
 * count as a fuzzy-duplicate signal (owner, 2026-09-25, D-143).
 */
export const FUZZY_DUPLICATE_DATE_WINDOW_DAYS = 7

/**
 * How many times the matching pass is asked again for the lines it left
 * without an Account or a required Marketing Category, or for the reason it
 * set supplied guidance aside (D-140, D-141, D-142). After these, the host's
 * own fallback runs.
 */
export const PASS2_REPAIR_ATTEMPTS = 2

/** The longest reason an AI may give for setting vendor guidance aside (D-140). */
export const GUIDANCE_REASON_MAX_LENGTH = 300

/**
 * Exact-match tolerance: ZERO. Since 2026-09-25 (`docs/decisions.md` D-151) it
 * feeds ONLY the prior-balance shape detectors in `backend/src/domain/amounts.ts`,
 * which pick a sentence for a flag's detail and never decide whether anything
 * reconciles. A near miss there correctly falls through to "verify manually".
 *
 * Every reconcile check reads `RECONCILE_TOLERANCE_CENTS` below instead.
 */
export const MONEY_TOLERANCE_CENTS = 0

/**
 * THE RECONCILE TOLERANCE: THREE CENTS, INCLUSIVE (owner, 2026-09-25 —
 * `docs/decisions.md` D-151, superseding D-130's "always exact"). A vendor
 * overcharged by a penny; the invoice could not be made to reconcile and still
 * match the document, and *"for a penny, that is more trouble than it is worth."*
 *
 * `|delta| <= 3` cents reconciles; 4 cents does not. ONE constant and ONE
 * predicate (`reconciles` in `money.ts`) feed every check that decides whether
 * money adds up: the `LineItemsToTotalMismatch` raise, the Import Control release gate,
 * the flag's Mark Resolved condition, the extraction backstop, and
 * the Point Click Care export guard. The
 * export uses the SAME predicate so it can never refuse what the modal
 * accepted — D-130's stranding, in which the two disagreed, cannot come back.
 *
 * The comparison it decides is ONE test, `lines + tax + shipping - total`
 * (`checkAmounts`, `backend/src/domain/amounts.ts`), which is the export guard's
 * own: the derived subtotal is not an input, so a stale one cannot let two
 * three-cent halves add up to a six-cent miss the export would refuse.
 *
 * The SPA (`app/src/lib/format.ts`) and the mock (`app/mock/db.ts`) import it.
 */
export const RECONCILE_TOLERANCE_CENTS = 3

/** The fuzzy-duplicate corpus window. The deterministic check is unbounded. */
export const DEDUP_WINDOW_DAYS = 90

/** Vendor candidates handed to the matching model, alias hits first. */
export const VENDOR_CANDIDATE_LIMIT = 50

/**
 * THE SHORT-ALIAS FLOOR for `company_aliases` (owner item 18, 2026-08-31),
 * counted on the company key — lower-cased with every non-alphanumeric
 * character removed — not on the raw text.
 *
 * The owner's own list runs down to `SLJ`, three characters, so three is the
 * floor rather than a rounder number. Two characters is refused: a company key
 * that short would be a plausible fragment of far too many printed names, and
 * an alias is compared for EQUALITY against a whole printed bill-to name, so
 * the damage a bad one does is a wrong company on every invoice that happens
 * to print those two characters alone.
 */
export const COMPANY_ALIAS_MIN_KEY_LENGTH = 3

/** Vendor-level precedent ACCOUNTS attached to every line item in the pass-2 input,
 *  most-used first. */
export const PRECEDENT_CANDIDATE_LIMIT = 10

/**
 * Past lines of the same vendor whose description SHARES AT LEAST ONE WORD
 * with this line's, attached to each line as `similar_candidates` — additive
 * to the per-vendor `precedent_candidates` channel.
 *
 * 25 (owner, 2026-09-15). This is a RETRIEVAL cap, not a confidence cutoff:
 * the host selects which past lines the model gets to read and orders them by
 * how many words they share, and the model alone decides whether any of them
 * fits. There is no score in the payload and no threshold — a pair either
 * shares a word or it does not.
 */
export const SIMILAR_CANDIDATE_LIMIT = 25

/**
 * The sole department under which `88-880` (Major Movable Equipment) and
 * `88-885` (Office Equipment) compose — item 8. A line below
 * `CAPITAL_EQUIPMENT_MINIMUM_CENTS` may not be coded to this department, and
 * therefore may not use either of those accounts either (there is no
 * other department for them to fall back to). Keyed on the department id, not
 * its name. The name "Capital Equipment" was once the loosest inference in
 * `data/masters/departments.md`, and is now confirmed by owner ruling
 * (`docs/decisions.md` D-21); the id is still the key because it is what the
 * chart and Point Click Care use.
 */
export const CAPITAL_EQUIPMENT_DEPARTMENT_ID = '190110'

/** $5,000, in cents — the floor below which the Capital Equipment pairing may not be used. */
export const CAPITAL_EQUIPMENT_MINIMUM_CENTS = 500_000

/** Memory coding rules read for one vendor, most recently confirmed first (D-367). */
export const PRECEDENT_LINE_ITEM_LIMIT = 500

/** A GL code splits on `-` into exactly three parts of these lengths. */
export const GL_CODE_SEGMENT_LENGTHS: readonly number[] = [6, 2, 3]

/**
 * Dropped from the printed vendor name before token-overlap scoring. Dropped from
 * the PRINTED side only — an intersection cannot score a token the printed side
 * lacks.
 */
export const VENDOR_NAME_STOP_WORDS: readonly string[] = [
  'inc',
  'llc',
  'corp',
  'corporation',
  'ltd',
  'co',
  'company',
  'the',
  'of',
  'and',
]

/** Every business comparison in this system is Eastern time. */
export const EASTERN_TIME_ZONE = 'America/New_York'

/** Request shape limits from the API contract. */
export const DEFAULT_PAGE_LIMIT = 50
export const MAX_PAGE_LIMIT = 200
export const MAX_IDS_PER_REQUEST = 200
export const UPLOAD_MAX_BYTES = 32 * 1024 * 1024
export const FLEET_IMPORT_MAX_BYTES = 10 * 1024 * 1024
/**
 * The vendor-list import cap (owner item 12) — 4 MiB, not the fleet import's
 * 10 MB. API Gateway base64-encodes a multipart body before a Lambda ever
 * sees it (`~4/3` overhead), and the handler runs synchronously behind
 * `ANY /{proxy+}`, so the real ceiling is Lambda's ~6 MB synchronous payload
 * limit applied to the ENCODED body, not the file. 4 MiB of file encodes to
 * ~5.3 MB, leaving headroom under that limit (INFERRED — nothing has run
 * against AWS yet). The real PCC export is 1.2 MB at 4,296 rows; the whole
 * 6,210-row master would still be comfortably under this cap.
 */
export const VENDOR_IMPORT_MAX_BYTES = 4 * 1024 * 1024
/**
 * The real vendor master can have up to ~2,000 vendors absent from one
 * upload (1,993 observed against the owner's real file) — too many to ship
 * whole in a preview response the way the fleet import's `absent` (typically
 * a couple dozen rows) does. Truncate the wire list to this many; the
 * response's own `absent_total` carries the true count.
 */
export const VENDOR_IMPORT_ABSENT_PREVIEW_LIMIT = 500
export const PRESIGN_TTL_SECONDS = 300
export const IDEMPOTENCY_WINDOW_HOURS = 24

// ---------------------------------------------------------------------------
// The Point Click Care CSV header — an immutable external contract
// ---------------------------------------------------------------------------

/**
 * Copied byte-for-byte from the deployed `export.ts`. This is the header of the
 * file that imported successfully into the facility Invoice Import: note
 * `Invoice #` / `Account #`, and that the row is FULLY quoted including
 * `Fiscal Year` and `Fiscal Period`, which are bare in data rows.
 */
export const INVOICE_CSV_HEADER =
  '"Vendor Code","Invoice #","Invoice Date","Fiscal Year","Fiscal Period","Transaction Amount","1099 Amount","Invoice Amount","Description","Account #"'

/** CRLF, and the file ends with one. No BOM. */
export const CSV_LINE_ENDING = '\r\n'

/** Thrown/returned when a set is not exportable; every problem is listed at once. */
export const EXPORT_BLOCKED_PREFIX = 'Not exported — fix these first: '

// ---------------------------------------------------------------------------
// Flag definitions — 28 invoice flags
// ---------------------------------------------------------------------------

/** Where the flag is *about*, which decides whether `detail` carries a line number. */
type FlagLevel = 'invoice' | 'line_item'

/** Where the flag's evidence comes from. Documentation, not behaviour. */
type FlagSignal =
  | 'pass 1'
  | 'pass 2'
  | 'deterministic'
  | 'deterministic + pass 2'
  | 'matching failure'

interface FlagDefinition {
  /** The stored `flags.flag_type` value. */
  readonly flag_type: string
  readonly label: string
  readonly level: FlagLevel
  readonly resolution: FlagResolution
  readonly signal: FlagSignal
  /** The in-app help text, verbatim — curly apostrophes and quotes included. */
  readonly description: string
}

/**
 * The 28 live invoice flags. The typography is verbatim from the deployed
 * strings: the curly apostrophes (U+2019), curly quotes (U+201C/U+201D) and em
 * dashes (U+2014) are part of them. Do not convert them to ASCII.
 *
 * Nothing here says "charge" — not a stored value, not a label, not a
 * description. `LineItemsToTotalMismatch`, `LineItemExtractionLowConfidence` and
 * `MaximumLineItemsExtracted` were renamed from `Charges…`/`Charge…` forms, so
 * any `flags.flag_type` row written under an old name needs migrating with the
 * seed. The QA corpus in `docs/qa/ground-truth.md` names the new values.
 *
 * This list has three sync points that must stay **byte-identical** for all 28
 * flags: this constant, the `flag_definitions` seed in `backend/db/schema.sql`,
 * and the table in `docs/flags.md`. The SPA imports this constant
 * (`app/src/lib/constants.ts`). `backend/src/domain/flagCopy.test.ts` fails the
 * build when they drift, or when the SPA writes its own copy again.
 *
 * `MatchingFailed` is the one flag not raised by the deterministic ladder or
 * either AI pass: the match worker writes it on its own failure path
 * (`backend/src/lambda/match.ts`'s `failMatching`) when pass 2 itself could not
 * be completed, so it can appear on an invoice with no other flag beside it —
 * the ladder never ran, so absence of the other flags proves nothing.
 *
 * `VendorRuleArbitrated` is RETIRED (owner ruling, 2026-08-18 — docs/decisions.md):
 * the AI arbitrating between a vendor's own conflicting coding rules is the
 * design, not something to flag. Nothing raises it any more and it is gone from
 * this list, from the app's mirror, and from `docs/flags.md`'s table — but its
 * `flag_definitions` seed row stays (schema.sql), because `flags.flag_type` is a
 * live foreign key on a database that is additive-only. The underlying fact —
 * which rule the AI picked between — is kept as the `VendorRuleArbitrated`
 * `account_source` stamp (schema.sql, `matchingLadder.ts`), not as a flag.
 *
 * `ReinstatedFromDenialReview` is RETIRED the same way (owner, 2026-10-08 —
 * docs/decisions.md D-324): a reinstate no longer returns the invoice to
 * Import Control, so there is no release for the flag to hold. Its seed row
 * stays for the same reason as `VendorRuleArbitrated`'s.
 *
 * `MissingAccount` and `MissingMarketingCategory` were retired by the code
 * review (D-267) and are live again (owner, 2026-10-03 — D-280: "there should
 * be a flag if a value is expected but was never written"): matching writes a
 * line it could not code, or a Marketing line it could not categorise, and
 * raises the flag instead of failing the invoice.
 */
export const FLAG_TYPES: readonly FlagDefinition[] = [
  {
    flag_type: 'MissingTotal',
    label: 'Missing Total',
    level: 'invoice',
    resolution: FLAG_RESOLUTION.auto,
    signal: 'deterministic',
    description: 'No invoice total was found. Please enter one and then mark this as resolved.',
  },
  {
    flag_type: 'MissingExternalInvoiceId',
    label: 'Missing External Invoice ID',
    level: 'invoice',
    resolution: FLAG_RESOLUTION.auto,
    signal: 'deterministic',
    description: 'No vendor-issued invoice number was found. The Invoice Date was applied instead. Please review and then mark this as resolved.',
  },
  {
    // Owner item 5, 2026-08-28: an invoice was advanced with no invoice date.
    // The date is required — `exportBuilder.ts` has always refused a CSV row
    // without one, four steps later; this flag moves that refusal upstream to
    // the first reviewer who can actually read it off the document.
    flag_type: 'MissingInvoiceDate',
    label: 'Missing Invoice Date',
    level: 'invoice',
    resolution: FLAG_RESOLUTION.auto,
    signal: 'deterministic',
    description: 'No invoice date was found. Please enter one and then mark this as resolved.',
  },
  {
    flag_type: 'LineItemsToTotalMismatch',
    label: 'Amounts Don’t Reconcile',
    level: 'invoice',
    resolution: FLAG_RESOLUTION.auto,
    signal: 'deterministic',
    description:
      'Line items plus tax plus shipping must equal the total to within $0.03. Please correct the amounts and then mark this as resolved.',
  },
  {
    flag_type: 'MissingVendor',
    label: 'Missing Vendor',
    level: 'invoice',
    resolution: FLAG_RESOLUTION.auto,
    signal: 'deterministic',
    description:
      'A vendor from our system could not be matched to this invoice. Please select one and then mark this as resolved.',
  },
  {
    flag_type: 'DuplicateVendor',
    label: 'Duplicate Vendor',
    level: 'invoice',
    resolution: FLAG_RESOLUTION.explicit,
    signal: 'deterministic',
    description:
      'Several vendors share this name; check that the selected one is right, then mark this as resolved.',
  },
  {
    flag_type: 'MissingCompany',
    label: 'Missing Company',
    level: 'invoice',
    resolution: FLAG_RESOLUTION.auto,
    signal: 'deterministic',
    description:
      'A company from our system could not be matched to this invoice. Please search and select one and then mark this as resolved.',
  },
  {
    // Raised per line by matching when every rung, the re-asks and the
    // fallback left the line with no Account (owner, 2026-10-03 — D-280).
    flag_type: 'MissingAccount',
    label: 'Missing Account',
    level: 'line_item',
    resolution: FLAG_RESOLUTION.auto,
    signal: 'deterministic',
    description:
      'One or more line items could not be assigned an Account. Please search and select one for each and then mark this as resolved.',
  },
  {
    flag_type: 'InvoiceExtractionLowConfidence',
    label: 'Low Invoice Confidence',
    level: 'invoice',
    resolution: FLAG_RESOLUTION.explicit,
    signal: 'pass 1',
    description:
      'One or more invoice-level fields are uncertain. Please verify them and then mark this as resolved.',
  },
  {
    flag_type: 'LineItemExtractionLowConfidence',
    label: 'Low Line Item Confidence',
    level: 'line_item',
    resolution: FLAG_RESOLUTION.explicit,
    signal: 'pass 1',
    description:
      'One or more line item-level fields are uncertain. Please verify them and then mark this as resolved.',
  },
  {
    flag_type: 'VendorMatchLowConfidence',
    label: 'Low Vendor Confidence',
    level: 'invoice',
    resolution: FLAG_RESOLUTION.explicit,
    signal: 'pass 2',
    description:
      'The selected vendor is uncertain. Please verify it and then mark this as resolved.',
  },
  {
    flag_type: 'CompanyMatchLowConfidence',
    label: 'Low Company Confidence',
    level: 'invoice',
    resolution: FLAG_RESOLUTION.explicit,
    signal: 'pass 2',
    description:
      'The selected company is uncertain. Please verify it and then mark this as resolved.',
  },
  {
    flag_type: 'AccountMatchLowConfidence',
    label: 'Low Account Confidence',
    level: 'line_item',
    resolution: FLAG_RESOLUTION.explicit,
    signal: 'pass 2',
    description:
      'The selected Account is uncertain. Please verify it and then mark this as resolved.',
  },
  {
    flag_type: 'NonUSDCurrency',
    label: 'Non-USD Currency',
    level: 'invoice',
    resolution: FLAG_RESOLUTION.explicit,
    signal: 'pass 1',
    description:
      'This invoice is not priced in US Dollars. Please verify the currency and the amounts, and then mark this as resolved.',
  },
  {
    flag_type: 'DuplicateDeterministic',
    label: 'Exact Duplicate Detected',
    level: 'invoice',
    resolution: FLAG_RESOLUTION.explicit,
    signal: 'deterministic',
    description:
      'This may be a duplicate of a prior invoice. Please either archive the invoice or mark this as resolved.',
  },
  {
    flag_type: 'DuplicateFuzzy',
    label: '“Fuzzy” Duplicate Detected',
    level: 'invoice',
    resolution: FLAG_RESOLUTION.explicit,
    signal: 'pass 2',
    description:
      'This may be a duplicate of a prior invoice. Please either archive the invoice or mark this as resolved.',
  },
  {
    flag_type: 'CreditMemo',
    label: 'Possible Credit',
    level: 'invoice',
    resolution: FLAG_RESOLUTION.explicit,
    signal: 'pass 1',
    description:
      'This invoice may contain a credit. Please verify and then mark this as resolved.',
  },
  {
    flag_type: 'UnrecognizedGLCode',
    label: 'Unrecognized Code',
    level: 'line_item',
    resolution: FLAG_RESOLUTION.explicit,
    signal: 'deterministic',
    description:
      'A code on the document does not match any code in our chart. Please verify the code and then mark this as resolved.',
  },
  {
    flag_type: 'GLCodeLineMismatch',
    label: 'Stamped Code May Not Fit Every Line',
    level: 'invoice',
    resolution: FLAG_RESOLUTION.explicit,
    signal: 'deterministic + pass 2',
    description:
      'One Code is stamped on this invoice for several of its lines, but the AI reads at least one of those lines as belonging to a different Account. Check each line’s Account, then mark this resolved.',
  },
  {
    flag_type: 'NonInvoice',
    label: 'Not A Standard Invoice',
    level: 'invoice',
    resolution: FLAG_RESOLUTION.explicit,
    signal: 'pass 1',
    description:
      'This document does not look like an invoice. Please either archive the invoice or mark this as resolved.',
  },
  {
    flag_type: 'CompanyInferredFromLetterhead',
    label: 'Company Inferred From Letterhead',
    level: 'invoice',
    resolution: FLAG_RESOLUTION.explicit,
    signal: 'pass 1',
    description:
      'The billed company was inferred from the letterhead, not a proper “bill to.” Please verify the company and then mark this as resolved.',
  },
  {
    flag_type: 'InternalDocument',
    label: 'Inter-Company Billing',
    level: 'invoice',
    resolution: FLAG_RESOLUTION.explicit,
    signal: 'deterministic',
    description:
      'This document’s vendor is also one of the companies in the system, so it may be one company billing another. Please just double-check that the Vendor and Company are correct.',
  },
  {
    flag_type: 'MaximumLineItemsExtracted',
    label: 'Maximum Number Of Line Items Extracted',
    level: 'invoice',
    resolution: FLAG_RESOLUTION.explicit,
    signal: 'pass 1',
    description: 'Please verify the line items and then mark this as resolved.',
  },
  {
    flag_type: 'MatchingFailed',
    label: 'Matching Failed',
    level: 'invoice',
    resolution: FLAG_RESOLUTION.explicit,
    signal: 'matching failure',
    description:
      'There was a system failure while processing this invoice. Please review all fields and then mark this as resolved.',
  },
  {
    flag_type: 'PriorBalanceIncluded',
    label: 'Prior Balance Included',
    level: 'invoice',
    resolution: FLAG_RESOLUTION.explicit,
    signal: 'pass 1',
    description:
      'The Total may include a balance from prior billing. Please confirm that only new line items are listed here and then mark this as resolved.',
  },
  {
    // Owner, 2026-09-14: an invoice naming vehicles was extracted, the vehicle
    // numbers were tagged, and they landed with an "Unmatched" pill because the
    // fleet held none of them. *"I think that this should raise a proper flag,
    // and I should be required to delete or correct unmatched tagged entities
    // before the invoice is allowed to proceed."*
    //
    // `auto`, not `explicit`: there is no judgment to record here. Either the
    // tag resolves to a vehicle or a person, or it is removed - and both of
    // those are edits the reviewer makes on the tag itself, which is exactly
    // what an auto flag reads. Since D-149 (2026-09-25) it is cleared by a Mark
    // Resolved click like every flag, and the click is refused until no tag is
    // left unmatched, so it still cannot be waved through.
    //
    // A PARTICIPANT TAG NEVER RAISES IT. Participant resolution was designed
    // and never built (`AssociatedEntitiesEditor.tsx`'s PORT NOTE), so a
    // participant row is as-printed text by design and can never carry a
    // reference. Counting one would block every invoice that mentions a
    // participant, permanently, with nothing the reviewer could do but delete
    // a true record.
    flag_type: 'UnmatchedTaggedEntity',
    label: 'Unmatched Tagged Entity',
    level: 'invoice',
    resolution: FLAG_RESOLUTION.auto,
    signal: 'deterministic',
    description:
      'One or more tagged entities could not be matched to our records. Please correct or remove each unmatched tag and then mark this as resolved.',
  },
  {
    // Raised once per invoice by matching when a line settled on a Marketing
    // account still has no Marketing Category after the re-ask (owner,
    // 2026-10-03 — D-280). `auto`: Mark Resolved is refused until every such
    // line has a category or has been re-coded off a Marketing account.
    flag_type: 'MissingMarketingCategory',
    label: 'Missing Marketing Category',
    // One row per invoice, naming no line, so `invoice` (it was `line_item`
    // before D-267, which no line-scoped code path ever read for it).
    level: 'invoice',
    resolution: FLAG_RESOLUTION.auto,
    signal: 'deterministic',
    description:
      'One or more line items are coded to PR, Marketing & Advertising and have no Marketing Category. Please pick one for each and then mark this as resolved.',
  },
  {
    // Raised once per invoice by matching when a `PACE Healthcare
    // Transportation` line still has no Transportation Category or no Fleet
    // Maintenance answer after the re-asks and the fallback (owner, 2026-10-03
    // — the D-280 rule applied to Transportation, D-282). `auto`: Mark
    // Resolved is refused until every such line is answered.
    flag_type: 'MissingTransportationDetails',
    label: 'Missing Transportation Details',
    level: 'invoice',
    resolution: FLAG_RESOLUTION.auto,
    signal: 'deterministic',
    description:
      'One or more line items are missing a Transportation Category or a Fleet Maintenance answer. Please complete both for each and then mark this as resolved.',
  },
]

/**
 * The 28 live stored flag values, as a union type for exhaustive switches.
 * `VendorRuleArbitrated` and `ReinstatedFromDenialReview` are retired (see the
 * note above `FLAG_TYPES`) and deliberately absent — nothing in the codebase
 * constructs one any more.
 */
export type FlagType =
  | 'MissingTotal'
  | 'MissingExternalInvoiceId'
  | 'MissingInvoiceDate'
  | 'LineItemsToTotalMismatch'
  | 'MissingVendor'
  | 'DuplicateVendor'
  | 'MissingCompany'
  | 'MissingAccount'
  | 'InvoiceExtractionLowConfidence'
  | 'LineItemExtractionLowConfidence'
  | 'VendorMatchLowConfidence'
  | 'CompanyMatchLowConfidence'
  | 'AccountMatchLowConfidence'
  | 'NonUSDCurrency'
  | 'DuplicateDeterministic'
  | 'DuplicateFuzzy'
  | 'CreditMemo'
  | 'UnrecognizedGLCode'
  | 'GLCodeLineMismatch'
  | 'NonInvoice'
  | 'CompanyInferredFromLetterhead'
  | 'InternalDocument'
  | 'MaximumLineItemsExtracted'
  | 'MatchingFailed'
  | 'PriorBalanceIncluded'
  | 'UnmatchedTaggedEntity'
  | 'MissingMarketingCategory'
  | 'MissingTransportationDetails'

const FLAG_BY_TYPE: ReadonlyMap<string, FlagDefinition> = new Map(
  FLAG_TYPES.map((f) => [f.flag_type, f]),
)

/** An unknown flag value falls back to its raw stored value. Keep the fallback. */
export function flagLabel(flagType: string): string {
  return FLAG_BY_TYPE.get(flagType)?.label ?? flagType
}

/** An unknown flag value defaults to `explicit`. Keep the fallback. */
export function flagResolution(flagType: string): FlagResolution {
  return FLAG_BY_TYPE.get(flagType)?.resolution ?? FLAG_RESOLUTION.explicit
}

/**
 * An age band. `max_days: null` is the open-ended top band. Bounds are
 * INCLUSIVE at both ends, which is why they read 0-1, 2-3 rather than 0-1, 1-3.
 */
export interface AgeBand {
  label: string
  min_days: number
  max_days: number | null
}

/**
 * THE KPI BAR'S OWN SCALE: one band per day to six, then everything past the
 * past-due threshold in one (owner, 2026-09-20: "the scale should be from 0 to
 * 7+ days").
 *
 * The bar asks how the outstanding work sits against the seven-day past-due
 * line. A scale running to `31+` spent most of its width on distinctions past
 * the only threshold anybody acts on.
 *
 * FOUR BANDS BECAUSE THE AGE RAMP HAS FOUR COLOURS, and no two bands may share
 * one (owner, 2026-09-20: "instead of doing 2 days and 3 days in the same
 * color, just do 2 - 3 days as one color. Reserve 7+ days to be its own
 * color"). The first cut of this scale ran one band per day to six plus `7+`,
 * which is eight bands over four hues: the renderer stretches the ramp across
 * whatever it is handed, so bands paired up two to a colour and a reader saw
 * four colours labelled eight ways. Merging each pair is what the owner asked
 * for, and it lands on four — so `7+` keeps red to itself, which is the half
 * of the instruction that matters most, since red is the past-due work.
 *
 * `4-6` IS THREE DAYS WIDE while the two below it are two. Merging the pairs
 * left `6` alone against a fifth colour the ramp does not have, and inventing
 * one would have put a third warm tone between orange and red — the two
 * hardest to tell apart already. Folding `6` upward keeps every band on its own
 * hue at the cost of one uneven width; splitting it out again is a one-line
 * change here plus a fifth entry in `AGE_RAMP`.
 *
 * KEEP THE COUNT AT OR UNDER `AGE_RAMP.length`. `app/mock/tests/dashboard-sections.test.ts`
 * asserts it, because exceeding it does not fail anything — it silently goes
 * back to two bands wearing one colour.
 *
 * THE TOP BAND IS THE PAST-DUE LINE. `PAST_DUE_DAYS` is 7 and this band opens
 * at 7, so the red segment is the past-due work — near enough to exact that the
 * bar and the card beside it finally read as one figure. The residue is a
 * fraction of a day: the card compares timestamps (`received_at < now - 7
 * days`) and the band compares whole days, so an invoice aged between seven
 * days and seven days plus a moment counts in the band and not yet in the card.
 * Before this change that same sliver sat in the GREEN band, which was the
 * worse way round — past-due work hiding in the reassuring colour.
 */
export const OUTSTANDING_AGE_BANDS: readonly AgeBand[] = [
  { label: '0-1', min_days: 0, max_days: 1 },
  { label: '2-3', min_days: 2, max_days: 3 },
  { label: '4-6', min_days: 4, max_days: 6 },
  { label: '7+', min_days: 7, max_days: null },
]
